"use client";

import type { GetAvailableClassResponse } from "@/openapi/generated";
import { formatTimeTo12Hour } from "@/lib/utils";
import { formatClassAgeRangeCompact } from "@/lib/utils/classPublicDisplay";

export function buildBaseDetails(cls: GetAvailableClassResponse) {
  const dayDetails =
    cls.classDays?.map((d) => ({
      day: d.day,
      time: `${formatTimeTo12Hour(d.startTime)} – ${formatTimeTo12Hour(
        d.endTime,
      )}`,
    })) ?? [];

  const otherDetails: { label: string; value: string }[] = [
    { label: "Location", value: cls.location?.name ?? "-" },
    { label: "Classroom", value: cls.classRoom?.name ?? "-" },
  ];

  if (cls.maxNumberOfStudents != null) {
    otherDetails.push({
      label: "Max Students",
      value: cls.maxNumberOfStudents.toString(),
    });
  }

  if (cls.currentNumberOfStudents != null) {
    otherDetails.push({
      label: "Currently Enrolled",
      value: cls.currentNumberOfStudents.toString(),
    });
  }

  if (cls.classStartDate && cls.classEndDate) {
    otherDetails.push({
      label: "Class Dates",
      value: `${new Date(cls.classStartDate).toLocaleDateString()} – ${new Date(
        cls.classEndDate,
      ).toLocaleDateString()}`,
    });
  }

  if (cls.classStartTime && cls.classEndTime) {
    otherDetails.push({
      label: "Class Time",
      value: `${formatTimeTo12Hour(
        cls.classStartTime,
      )} – ${formatTimeTo12Hour(cls.classEndTime)}`,
    });
  }

  return {
    dayDetails,
    otherDetails,
    description: cls.description,
  };
}

export function buildFullDetails(data: any) {
  const dayDetails =
    data.classDays?.map((d: any) => ({
      day: d.day,
      time: `${formatTimeTo12Hour(d.startTime)} – ${formatTimeTo12Hour(
        d.endTime,
      )}`,
    })) ?? [];

  const teachers = data.teachers?.length
    ? data.teachers.map((t: any) => `${t.firstName} ${t.lastName}`).join("\n")
    : "-";

  const otherDetails: { label: string; value: string }[] = [
    {
      label: data.teachers?.length > 1 ? "Teachers" : "Teacher",
      value: teachers,
    },
    { label: "Classroom", value: data.classRoom?.name ?? "-" },
    { label: "Location", value: data.location?.name ?? "-" },
    {
      label: "Age Range",
      value: formatClassAgeRangeCompact(data.minAge, data.maxAge) ?? "-",
    },
    { label: "Gender", value: data.genderComposition ?? "-" },
    { label: "Season", value: data.season?.name ?? "-" },
  ];

  if (data.maxNumberOfStudents != null) {
    otherDetails.push({
      label: "Max Students",
      value: data.maxNumberOfStudents.toString(),
    });
  }

  if (data.currentNumberOfStudents != null) {
    otherDetails.push({
      label: "Currently Enrolled",
      value: data.currentNumberOfStudents.toString(),
    });
  }

  return {
    dayDetails,
    otherDetails,
    description: data.description,
  };
}
